import React, { useState } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BACKEND_URL } from '../lib/api';

/**
 * /reset-password/:token — consumes the one-hour reset token, sets a new
 * password, then routes the user to /login with a success toast.
 */
const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    if (pw !== confirm) { toast.error('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success('Password updated. Please sign in with your new password.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset your password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-stone-50 flex items-center justify-center px-4 py-12" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white border border-stone-200 rounded-2xl shadow-sm p-8 sm:p-10">
        {done ? (
          <div className="text-center" data-testid="reset-password-success">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-700" />
            </div>
            <h1 className="font-serif text-2xl text-[#1F3D2B] mb-2">Password updated</h1>
            <p className="text-stone-600 text-sm">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-[#1F3D2B]/5 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-5 h-5 text-[#1F3D2B]" />
            </div>
            <h1 className="font-serif text-3xl text-[#1F3D2B] mb-2 text-center" data-testid="reset-password-heading">
              Choose a new password
            </h1>
            <p className="text-stone-600 text-sm leading-relaxed mb-8 text-center">
              Pick something memorable. At least 8 characters, including one uppercase letter.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="pw" className="text-[#4A5D50]">New password</Label>
                <Input
                  id="pw"
                  type="password"
                  required
                  minLength={8}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="mt-1"
                  data-testid="reset-password-new"
                />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-[#4A5D50]">Confirm password</Label>
                <Input
                  id="confirm"
                  type="password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1"
                  data-testid="reset-password-confirm"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full py-6 font-medium shadow-md"
                data-testid="reset-password-submit"
              >
                {loading ? 'Updating…' : 'Update password'}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-[#4A5D50] hover:text-[#1F3D2B] hover:underline">
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
